// / <reference path="./index.d.ts" />
import urlParameter from '@hc-frontend/shared-assets/src/js/utils/url/urlParameter'
import getPlanByPlanId from 'core/controllerPlans/getPlanByPlanId'
import initController from 'core/controllerPlans/initController'

import appStateUpdated from 'utils/appStateUpdated'
import isBTMPlan from 'utils/pivot/plan/isBTMPlan'
import { isArray } from '@hc-frontend/utils'
import store from 'utils/store/withMiddleware'
import once from 'utils/functional/once'
import { COMPARE_PLANS, QUOTES } from 'sites/routes'
import parsePlanFromResponse from './controller'
import 'bootstrap/js/dist/dropdown'
import updateDOM from './updateDOM'
import initDOM from './initDOM'
import finish from './finish'
import config from './config'
import './styles.scss'
import { recEngineResponseParser } from 'utils/pivot/recEngineParser'
import findPlanByLegacyId from 'utils/pivot/plan/findPlanByLegacyId'
import { IS_ENROLL } from 'core/constants/environment'
import applyRules from './controller/applyRules'
import generatePlanFilter from 'utils/pivot/plan/generatePlanFilter'
import addPlanAdditionalProperties from 'sites/pivothealth.com/short-term-health-insurance/consumer/quotes/controller/addPlanAdditionalProperties'
import findPlanById from 'utils/pivot/plan/findPlanById'
import createAndSaveQuoteMetadata from 'core/controllerPlans/create-and-save-quote-metadata'

function getDataFromUrlPlans(responsePlans, urlPlansIds: string[]): ComparePage.Plan[] {
  return urlPlansIds
    .map(urlPlanRequest => {
      const { plans, plansSTM = [], plansBTM = [] } = responsePlans

      const plansMultiCarrier = Object.entries(plans)
        .map(plan => plan[1])
        .filter(plan => plan?.multiCarrierPlan)

      const urlPlanIdParts = urlPlanRequest.split('-')
      const [urlPlanId, urlPlanPeriod, urlPlanPolicies] = urlPlanIdParts
      const isNewId = urlPlanIdParts.length === 2 || urlPlanIdParts.length === 3
      const plan = isNewId
        ? {
            id: urlPlanId,
            period: parseInt(urlPlanPeriod, 10),
            policies: parseInt(urlPlanPolicies, 10) || 1
          }
        : {
            ...findPlanByLegacyId(plansSTM, plans, urlPlanRequest),
            policies: 1
          }

      const planList = [
        ...plansSTM,
        ...plansBTM,
        ...plansMultiCarrier.map(plan => plan.planId)
      ]

      const urlPlan = getPlanByPlanId({
        planList,
        plans,
        ...plan
      })
      if (typeof urlPlan.id !== 'undefined') {
        return {
          plan: parsePlanFromResponse(
            responsePlans,
            urlPlan.id,
            urlPlan.period,
            plan.policies
          ),
          hasChanged: urlPlan.hasChanged
        }
      }
    })
    .filter(Boolean)
}

function redirectError() {
  window.location = QUOTES
}

function handleResponse([responsePlans, recEngine]) {
  const urlPlansIds = urlParameter('plans').split(',')

  if (urlPlansIds.length === 0 || (responsePlans.error && responsePlans.error.length)) {
    redirectError()
  } else {
    const recEnginePlans = {}

    if (!isArray(responsePlans.plansBTM)) {
      if (IS_ENROLL) {
        const recommendedPlanMeta = recEngineResponseParser(recEngine, false)
        Object.keys(recommendedPlanMeta).forEach(planId => {
          const newPlanId = findPlanById(
            responsePlans.plansSTM,
            +planId,
            responsePlans.plans,
            undefined
          ).id

          if (newPlanId) {
            const parsedPlan = parsePlanFromResponse(
              responsePlans,
              newPlanId,
              recommendedPlanMeta[planId].duration,
              recommendedPlanMeta[planId].policies
            )
            recEnginePlans[`${newPlanId}-${parsedPlan.period}-${parsedPlan.policies}`] =
              parsedPlan
          }
        })
      } else {
        recEngine.data.default?.forEach(id => {
          if (!responsePlans.plans[id]) {
            return
          }
          const maxDuration = Math.max(...Object.keys(responsePlans.plans[id].durations))
          const durations = responsePlans.plans[id].durations[maxDuration]
          const policies = Math.max(...Object.keys(durations))
          const parsedPlan = parsePlanFromResponse(
            responsePlans,
            id,
            maxDuration,
            policies
          )
          recEnginePlans[`${id}-${parsedPlan.period}-${parsedPlan.policies}`] = parsedPlan
        })
      }
    }

    const urlPlanDataArray = getDataFromUrlPlans(responsePlans, urlPlansIds)

    const planHasChanged = urlPlanDataArray.some(({ hasChanged }) => Boolean(hasChanged))

    const urlPlanData = urlPlanDataArray.reduce((acc, { plan }) => {
      return {
        ...acc,
        [`${plan.planId}-${plan.period}-${plan.policies}`]: plan
      }
    }, {})

    const plansToShow = Object.keys(urlPlanData)

    const plans = {
      ...recEnginePlans,
      ...urlPlanData
    }

    const filtersByProductType = plansToShow
      .map(planKey => {
        const [planId, period, policies] = planKey.split('-')

        return generatePlanFilter({
          plan: responsePlans.plans[planId],
          policies: +policies,
          period: +period
        })
      })
      .reduce((acc, filter, index) => {
        return {
          ...acc,
          [index]: filter
        }
      }, {})

    if (urlPlanDataArray.length === 0) {
      redirectError()
    } else {
      const compareBTM = urlPlanDataArray.some(isBTMPlan)
      store.setMiddleWare(applyRules)
      store.registry([once(initDOM), updateDOM, once(finish), appStateUpdated])
      store.update({
        plansToShow,
        defaultPlans: plans,
        plansData: plans,
        plans: addPlanAdditionalProperties(responsePlans.plans),
        removedPlans: [],
        quoteKey: responsePlans.quoteKey,
        compareBTM,
        filterOptionsByProductType: responsePlans.filterOptionsByProductType,
        plansSTM: responsePlans.plansSTM,
        carriers: responsePlans.carriers,
        brochures: responsePlans.brochures,
        planHasChanged,
        filtersByProductType
      })
    }
  }
}

initController(config)
  .then(handleResponse)
  .then(createAndSaveQuoteMetadata(COMPARE_PLANS))
